/**
 * Group Redux Slice
 * Manages group state and async operations
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { groupService } from '../entities/group/api/groupService';
import { Group, CreateGroupDTO, UpdateGroupDTO, GroupState } from '../entities/group/model/types';

const initialState: GroupState = {
  groups: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchGroupsForBoard = createAsyncThunk(
  'groups/fetchGroupsForBoard',
  async (boardId: number, { rejectWithValue }) => {
    try {
      return await groupService.getGroupsForBoard(boardId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch groups');
    }
  }
);

export const createGroup = createAsyncThunk(
  'groups/createGroup',
  async (groupData: CreateGroupDTO, { rejectWithValue }) => {
    try {
      return await groupService.createGroup(groupData);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create group');
    }
  }
);

export const updateGroup = createAsyncThunk(
  'groups/updateGroup',
  async ({ groupId, updates }: { groupId: number; updates: UpdateGroupDTO }, { rejectWithValue }) => {
    try {
      return await groupService.updateGroup(groupId, updates);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update group');
    }
  }
);

export const deleteGroup = createAsyncThunk(
  'groups/deleteGroup',
  async (groupId: number, { rejectWithValue }) => {
    try {
      await groupService.deleteGroup(groupId);
      return groupId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete group');
    }
  }
);

export const reorderGroups = createAsyncThunk(
  'groups/reorderGroups',
  async ({ boardId, groupIds }: { boardId: number; groupIds: number[] }, { rejectWithValue }) => {
    try {
      return await groupService.reorderGroups(boardId, groupIds);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reorder groups');
    }
  }
);

export const toggleGroupCollapse = createAsyncThunk(
  'groups/toggleCollapse',
  async (groupId: number, { rejectWithValue }) => {
    try {
      return await groupService.toggleCollapse(groupId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle group');
    }
  }
);

// Slice
const groupSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    clearGroups: (state) => {
      state.groups = [];
      state.error = null;
    },
    clearGroupError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch groups
    builder.addCase(fetchGroupsForBoard.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchGroupsForBoard.fulfilled, (state, action: PayloadAction<Group[]>) => {
      state.loading = false;
      state.groups = action.payload;
    });
    builder.addCase(fetchGroupsForBoard.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create group
    builder.addCase(createGroup.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createGroup.fulfilled, (state, action: PayloadAction<Group>) => {
      state.loading = false;
      state.groups.push(action.payload);
    });
    builder.addCase(createGroup.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update group
    builder.addCase(updateGroup.fulfilled, (state, action: PayloadAction<Group>) => {
      const index = state.groups.findIndex((grp) => grp.id === action.payload.id);
      if (index !== -1) {
        state.groups[index] = action.payload;
      }
    });

    // Delete group
    builder.addCase(deleteGroup.fulfilled, (state, action: PayloadAction<number>) => {
      state.groups = state.groups.filter((grp) => grp.id !== action.payload);
    });

    // Reorder groups
    builder.addCase(reorderGroups.fulfilled, (state, action: PayloadAction<Group[]>) => {
      state.groups = action.payload;
    });

    // Toggle collapse
    builder.addCase(toggleGroupCollapse.fulfilled, (state, action: PayloadAction<Group>) => {
      const index = state.groups.findIndex((grp) => grp.id === action.payload.id);
      if (index !== -1) {
        state.groups[index] = action.payload;
      }
    });
  },
});

export const { clearGroups, clearGroupError } = groupSlice.actions;
export default groupSlice.reducer;
